import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Save,
  RotateCcw,
  Calendar,
  GraduationCap,
  CheckCircle2,
  Bell,
  Info,
  ChevronDown,
} from 'lucide-react';

import { RootState } from '../../store';
import {
  useLazyToolsSettingsQuery,
  useUpdateSettingMutation,
  useResetSettingsMutation,
} from './settingsApi';
import { setConfigurationsSettings } from './settingsSlice';
import Loader from '../../ui-components/Loader';
import ConfirmationModal from '../../ui-components/ConfirmationModal';

// Configuration group tags for easier maintenance
const CONFIG_GROUPS = {
  ROOM_BOOKING_NOTIFICATIONS: 'Room Booking Notifications',
  TRAINING_NOTIFICATIONS: 'Training Notifications',
  THROTTLING_LIMIT: 'NOTIFICATION_THROTTLING_LIMIT',
} as const;

const TABS = [
  {
    id: 'booking',
    label: 'Room Booking',
    icon: Calendar,
    groupKeywords: ['Booking'],
  },
  {
    id: 'training',
    label: 'Training',
    icon: GraduationCap,
    groupKeywords: ['Training'],
  },
  {
    id: 'notification',
    label: 'Notification',
    icon: Bell,
    groupKeywords: [
      'Notification',
      'Notify',
      CONFIG_GROUPS.ROOM_BOOKING_NOTIFICATIONS,
    ],
  },
];

interface SettingInstance {
  name: string;
  description: string;
  value: string;
  groupType: string;
  displayType: string;
  helpText: string;
  sourceValue?: any;
}

const ConfigurationsSettingPage = () => {
  const dispatch = useDispatch();
  const [toolsSettingsApi, { isLoading }] = useLazyToolsSettingsQuery();
  const [updateSetting, { isLoading: isUpdating }] = useUpdateSettingMutation();
  const [resetSettings, { isLoading: isResetting }] =
    useResetSettingsMutation();

  const configurations =
    useSelector((store: RootState) => store.settings.configurations) || [];
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState('booking');
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const res = await toolsSettingsApi({
          type: 'configurations',
          _t: Date.now(),
        }).unwrap();
        if (res?.settings?.status === 200) {
          dispatch(setConfigurationsSettings(res.data));

          const initialValues: Record<string, string> = {};
          res.data.forEach((item: SettingInstance) => {
            initialValues[item.name] = item.value;
          });
          setFormValues(initialValues);
        }
      } catch (error) {
        console.error('Failed to fetch configurations:', error);
        toast.error('Failed to load settings');
      }
    }
    load();
  }, [dispatch, toolsSettingsApi]);

  const handleInputChange = (name: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      // Only send changed values for existing settings
      const payload = {
        setting: formValues,
      };
      await updateSetting(payload).unwrap();

      // Refresh settings in redux
      const updatedSettings = configurations.map((item: any) => ({
        ...item,
        value: formValues[item.name] ?? item.value,
      }));
      dispatch(setConfigurationsSettings(updatedSettings));

      toast.success('Configurations saved successfully');
    } catch (error: any) {
      const message =
        error?.data?.settings?.message || 'Failed to save configurations';
      toast.error(message);
    }
  };

  const handleResetToDefault = async () => {
    setIsResetModalOpen(true);
  };

  const confirmReset = async () => {
    const activeTabConfig = TABS.find((t) => t.id === activeTab);
    try {
      await resetSettings({
        type: 'configurations',
        groupKeywords: activeTabConfig?.groupKeywords,
      }).unwrap();

      // Re-fetch to get the new default values
      const res = await toolsSettingsApi({ type: 'configurations' }).unwrap();
      if (res?.settings?.status === 200) {
        dispatch(setConfigurationsSettings(res.data));
        const initialValues: Record<string, string> = {};
        res.data.forEach((item: SettingInstance) => {
          initialValues[item.name] = item.value;
        });
        setFormValues(initialValues);
        toast.success('Configurations reset to system defaults');
      }
    } catch (error: any) {
      toast.error('Failed to reset configurations');
    } finally {
      setIsResetModalOpen(false);
    }
  };

  // Filter and group configurations based on the active tab
  const groupedData = useMemo(() => {
    const activeTabConfig = TABS.find((t) => t.id === activeTab);
    if (!activeTabConfig) return [];

    // Filter items that belong to the active tab's groups
    const filtered = configurations.filter((item) => {
      // Always show Room Booking Notifications in Notification tab
      if (
        item.groupType === CONFIG_GROUPS.ROOM_BOOKING_NOTIFICATIONS &&
        activeTab === 'notification'
      ) {
        return true;
      }

      // Never show Room Booking Notifications in other tabs
      if (item.groupType === CONFIG_GROUPS.ROOM_BOOKING_NOTIFICATIONS) {
        return false;
      }

      // Always show Training Notifications in Notification tab
      if (
        item.groupType === CONFIG_GROUPS.TRAINING_NOTIFICATIONS &&
        activeTab === 'notification'
      ) {
        return true;
      }

      // Never show Training Notifications in other tabs
      if (item.groupType === CONFIG_GROUPS.TRAINING_NOTIFICATIONS) {
        return false;
      }

      // Never show Throttling Limit in any tab
      if (item.name === CONFIG_GROUPS.THROTTLING_LIMIT) {
        return false;
      }

      // Normal filtering for other items
      return activeTabConfig.groupKeywords.some((keyword) =>
        item.groupType.toLowerCase().includes(keyword.toLowerCase())
      );
    });

    // Group by groupType
    const groups: Record<string, SettingInstance[]> = {};
    filtered.forEach((item) => {
      if (!groups[item.groupType]) {
        groups[item.groupType] = [];
      }
      groups[item.groupType].push(item);
    });

    return Object.entries(groups).map(([name, items]) => ({
      group: name,
      items: items, // Honor backend orderBy
    }));
  }, [configurations, activeTab]);

  if (isLoading) return <Loader />;

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-brand-50 dark:bg-brand-900/20 border border-brand-100 dark:border-brand-800/50 rounded-xl p-4 flex gap-4 items-start w-full">
        <div className="bg-brand-100 dark:bg-brand-800/50 p-2 rounded-lg">
          <Info size={20} className="text-brand-600 dark:text-brand-400" />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-brand-900 dark:text-white">
            System-Wide Configuration Tokens
          </h4>
          <p className="text-sm text-brand-700 dark:text-neutral-300 mt-1">
            These configuration tokens control policy, constraints, thresholds,
            and behavioral parameters across the system. Changes apply
            prospectively only and do not affect existing records.
          </p>
        </div>
      </div>

      <div className="bg-neutral-100 dark:bg-neutral-900/50 p-1.5 rounded-xl flex gap-1 w-full">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2.5 px-6 py-2.5 text-sm font-bold rounded-lg transition-all duration-200 ${
                isActive
                  ? 'bg-white dark:bg-neutral-800 text-black dark:text-white shadow-sm'
                  : 'text-neutral-500 dark:text-neutral-400 hover:text-black dark:hover:text-neutral-200'
              }`}
            >
              <Icon
                size={18}
                className={
                  isActive
                    ? 'text-black dark:text-white'
                    : 'text-neutral-500 dark:text-neutral-400'
                }
              />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="space-y-6">
        {groupedData.length > 0 ? (
          groupedData.map((group) => (
            <div
              key={group.group}
              className="rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-950 overflow-hidden"
            >
              <div className="px-8 py-5 border-b border-neutral-100 dark:border-neutral-800">
                <h2 className="text-lg font-bold text-neutral-900 dark:text-white">
                  {group.group}
                </h2>
              </div>

              <div className="p-8">
                <div className="flex flex-col gap-8">
                  {group.items.map((item: SettingInstance) => (
                    <div key={item.name} className="flex flex-col gap-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <label
                            htmlFor={item.name}
                            className="text-[15px] font-normal text-neutral-500 dark:text-neutral-400"
                          >
                            {item.description}
                          </label>
                        </div>

                        {item.displayType.toLowerCase() === 'checkbox' && (
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={formValues[item.name] === 'Y'}
                              onChange={(e) =>
                                handleInputChange(
                                  item.name,
                                  e.target.checked ? 'Y' : 'N'
                                )
                              }
                              disabled={isUpdating}
                            />
                            <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none rounded-full peer dark:bg-neutral-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-neutral-600 peer-checked:bg-brand-600"></div>
                          </label>
                        )}
                      </div>

                      {item.displayType.toLowerCase() !== 'checkbox' && (
                        <div className="flex flex-col gap-2">
                          {item.displayType.toLowerCase() === 'select' ||
                          item.displayType.toLowerCase() === 'selectbox' ? (
                            <div className="relative">
                              <select
                                value={formValues[item.name] || ''}
                                onChange={(e) =>
                                  handleInputChange(item.name, e.target.value)
                                }
                                disabled={isUpdating}
                                className="w-full h-12 px-4 appearance-none bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all text-sm text-neutral-900 dark:text-neutral-200"
                              >
                                {Array.isArray(item.sourceValue) ? (
                                  item.sourceValue.map((opt: any) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.name}
                                    </option>
                                  ))
                                ) : (
                                  <option value={formValues[item.name]}>
                                    {formValues[item.name]}
                                  </option>
                                )}
                              </select>
                              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-400 dark:text-neutral-500">
                                <ChevronDown size={18} />
                              </div>
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={formValues[item.name] || ''}
                              onChange={(e) =>
                                handleInputChange(item.name, e.target.value)
                              }
                              disabled={isUpdating}
                              className="w-full h-12 px-4 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all text-sm text-neutral-900 dark:text-neutral-200"
                            />
                          )}
                        </div>
                      )}
                      {item.helpText && (
                        <p className="text-sm text-neutral-500 dark:text-neutral-400/80">
                          {item.helpText}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="p-12 text-center bg-white dark:bg-neutral-950 rounded-2xl border border-dashed border-neutral-300 dark:border-neutral-800">
            <p className="text-neutral-500">
              No configuration tokens found for this category.
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 pb-8">
        <button
          type="button"
          onClick={handleResetToDefault}
          disabled={isUpdating || isResetting}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-all shadow-sm"
        >
          <RotateCcw size={18} />
          {isResetting ? 'Resetting...' : 'Reset to Default'}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isUpdating}
          className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 active:scale-95 transition-all shadow-md shadow-brand-500/20"
        >
          <Save size={18} />
          {isUpdating ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      <ConfirmationModal
        isOpen={isResetModalOpen}
        title={`Reset ${
          TABS.find((t) => t.id === activeTab)?.label
        } Configurations`}
        message={`Are you sure you want to reset all ${
          TABS.find((t) => t.id === activeTab)?.label
        } configurations to their system default values? Settings in other tabs will remain unchanged.`}
        confirmLabel="Reset to Default"
        cancelLabel="Cancel"
        onConfirm={confirmReset}
        onCancel={() => setIsResetModalOpen(false)}
        isLoading={isResetting}
        tone="warning"
      />
    </div>
  );
};

export default ConfigurationsSettingPage;
